import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, lastValueFrom } from 'rxjs';
import { PaymentCard } from '../../models/payment-card.model';
import { DataService } from '../../services/data.service';
import { PaymentService } from '../../services/payment.service';
import { SpinnerService } from '../../services/spinner.service';
import { AuthService } from '../../services/auth.service';
import { VerificationService } from '../../services/verification.service';
import { PaymentCardListComponent } from '../payment-card-list/payment-card-list.component';
import { ButtonComponent } from '../../shared/reusable/button/button.component';
import { InputErrorComponent } from '../common/input-error/input-error.component';
import { IsControlInvalidPipe } from '../../pipes/is-control-invalid.pipe';
import { createMask, InputMaskModule } from '@ngneat/input-mask';

@Component({
  selector: 'app-add-payment-card',
  templateUrl: './add-payment-card.component.html',
  styleUrls: ['./add-payment-card.component.scss'],
  imports: [
    CommonModule,
    PaymentCardListComponent,
    ButtonComponent,
    ReactiveFormsModule,
    InputErrorComponent,
    IsControlInvalidPipe,
    InputMaskModule,
  ],
  standalone: true,
})
export class AddPaymentCardComponent implements OnInit, OnDestroy {
  public showCustomForm = false;
  public paymentCards: PaymentCard[] = [];
  public cardSelected: PaymentCard | undefined;
  private destroyRef$ = new Subject<boolean>();
  public loading = false;
  public customCardForm!: FormGroup<{
    card_number: FormControl<string | undefined>;
    month: FormControl<string | undefined>;
    year: FormControl<string | undefined>;
    cvv: FormControl<string | undefined>;
    nombre: FormControl<string | undefined>;
  }>;

  cardNumberMask = createMask({
    mask: '9999 9999 9999 9999',
    placeholder: '',
  });

  expirationMonthMask = createMask({
    mask: '99',
    placeholder: 'MM',
  });

  expirationYearMask = createMask({
    mask: '99',
    placeholder: 'AA',
  });

  cvvMask = createMask({
    mask: '999',
    placeholder: '',
  });

  constructor(
    private dataService: DataService,
    private paymentService: PaymentService,
    private router: Router,
    private spinner: SpinnerService,
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private verificationService: VerificationService,
  ) {}

  ngOnInit(): void {
    this.getCards();
    this.initForm();
  }

  private initForm(): void {
    this.customCardForm = this.formBuilder.group({
      card_number: new FormControl<string | undefined>(undefined, {
        validators: [Validators.required, Validators.minLength(19)],
        nonNullable: true,
      }),
      month: new FormControl<string | undefined>(undefined, {
        validators: [Validators.required, Validators.minLength(2), Validators.maxLength(2)],
        nonNullable: true,
      }),
      year: new FormControl<string | undefined>(undefined, {
        validators: [Validators.required, Validators.minLength(2)],
        nonNullable: true,
      }),
      cvv: new FormControl<string | undefined>(undefined, {
        validators: [Validators.required, Validators.minLength(3)],
        nonNullable: true,
      }),
      nombre: new FormControl<string | undefined>(undefined, {
        validators: [Validators.required],
        nonNullable: true,
      }),
    });
  }

  private getCards(): void {
    this.loading = true;
    this.spinner.show();
    this.paymentService.getCards().subscribe({
      next: (cards: PaymentCard[]) => {
        this.cardSelected = cards.find((c: PaymentCard) => c.selected);
        this.paymentCards = cards;
        this.loading = false;
        this.spinner.hide();
      },
      error: () => {
        this.loading = false;
        this.spinner.hide();
        alert('No se pudieron cargar las tarjetas. Intenta nuevamente.');
      },
    });
  }

  public onSelectCard(paymentCard: PaymentCard): void {
    this.cardSelected = paymentCard;
  }

  public openCustomForm(): void {
    this.showCustomForm = true;
  }

  public closeCustomForm(): void {
    this.showCustomForm = false;
    this.customCardForm.reset();
  }

  public async submitCustomCard(): Promise<void> {
    const formValue = this.customCardForm.value;
    const verificationCode = this.authService.getVerificationCode();

    if (!verificationCode) {
      alert('Código de verificación no encontrado');
      return;
    }

    const payload = {
      numero_tarjeta: formValue.card_number?.replace(/\s/g, '') || '',
      nombre_titular: formValue.nombre || '',
      mes_expiracion: formValue.month || '',
      ano_expiracion: formValue.year || '',
      cvv: formValue.cvv || '',
      verification_code: verificationCode,
    };

    this.spinner.show();

    try {
      const response = await lastValueFrom(
        this.paymentService.tokenizeCard(payload)
      );

      if (response.status === 'ok') {
        const data = response.data;

        if (Array.isArray(data) && data.length > 0 && data[0].tarjetaTokenizada === 'NO') {
          this.spinner.hide();
          alert('Tu tarjeta debe estar habilitada por tu banco para compras online / e-commerce');
          return;
        }

        this.spinner.hide();
        alert('La tarjeta ha sido agregada exitosamente.');
        this.closeCustomForm();
        this.getCards();
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
    } catch (error: any) {
      this.spinner.hide();
      const errorMessage = error?.error?.message || 'No se pudo tokenizar la tarjeta. Intenta nuevamente.';
      alert(errorMessage);
    }
  }

  public isCustomFormInvalid(): boolean {
    return !this.customCardForm.valid;
  }

  public async confirm(): Promise<void> {
    if (!this.cardSelected) {
      return;
    }

    const verificationCode = this.authService.getVerificationCode();
    if (!verificationCode) {
      alert('Código de verificación no encontrado');
      return;
    }

    try {
      this.spinner.show();
      this.dataService.setSelectedCard(this.cardSelected);

      const status = await lastValueFrom(this.verificationService.getStatus(verificationCode));
      this.dataService.setVerificationStatus(status.data);

      this.spinner.hide();

      if (status.data.biometric_required && !status.data.biometric_completed) {
        this.router.navigate(['/biometric-check']);
      } else {
        this.router.navigate(['/summary']);
      }
    } catch (error) {
      this.spinner.hide();
      console.error('Error checking verification status:', error);
      alert('No se pudo confirmar la selección de la tarjeta. Intenta nuevamente.');
    }
  }

  ngOnDestroy(): void {
    this.destroyRef$.next(true);
    this.destroyRef$.complete();
  }
}
